import { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  Box,
  Paper,
  Divider,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ClienteSearch from "../../utils/ClienteSearch";
import ServicioSearch from "../../utils/ServicioSearch";

// Iconos para diferentes módulos
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import RouteIcon from "@mui/icons-material/Route";
import ForumIcon from "@mui/icons-material/Forum";

/**
 * Componente para la configuración de filtros de notificaciones
 */
const NotificationSettings = ({ currentUser, currentService, adminData }) => {
  // Estados para la configuración de filtros
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // Configuración de módulos con canal de notificación por email
  const [moduleSettings, setModuleSettings] = useState({
    documentos: { enabled: false, email: false },
    cuadrantes: { enabled: false, email: false },
    usuarios: { enabled: false, email: false },
    clientes: { enabled: false, email: false },
    servicios: { enabled: false, email: false },
    jornadas: { enabled: false, email: false },
    controlRondas: { enabled: false, email: false },
    buzónAnónimo: { enabled: false, email: false } // Nuevo módulo
  });

  // Clientes y servicios
  const [clientes, setClientes] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);

  // Servicios (tiendas)
  const [servicios, setServicios] = useState([]);
  const [selectedServicios, setSelectedServicios] = useState([]);

  // Estado para el snackbar de guardado
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Definición de módulos y sus iconos
  const modules = [
    { id: 'documentos', name: 'Documentos', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { id: 'cuadrantes', name: 'Cuadrantes', icon: <CalendarMonthIcon fontSize="small" /> },
    { id: 'vigilantes', name: 'Usuarios', icon: <BadgeOutlinedIcon fontSize="small" /> },
    { id: 'clientes', name: 'Clientes', icon: <GroupsOutlinedIcon fontSize="small" /> },
    { id: 'servicios', name: 'Servicios', icon: <StoreOutlinedIcon fontSize="small" /> },
    { id: 'jornadas', name: 'Control Horario', icon: <QueryBuilderIcon fontSize="small" /> },
    { id: 'controlRondas', name: 'Control de Rondas', icon: <RouteIcon fontSize="small" /> },
    { id: 'buzónAnónimo', name: 'Buzón Anónimo', icon: <ForumIcon fontSize="small" /> } // Nuevo módulo
  ];

  // Efecto para cargar configuración de filtros
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return;

    const loadUserFilters = async () => {
      try {
        setInitialLoading(true);
        // Referencia al documento de configuración de filtros del usuario
        const userFilterRef = doc(
          db,
          "servicios",
          currentService.id,
          "usuarios_filtros",
          currentUser.email
        );

        const filterDoc = await getDoc(userFilterRef);

        if (filterDoc.exists()) {
          const data = filterDoc.data();

          // Cargar configuración general
          setNotificationsEnabled(data.notificationsEnabled !== false); // Por defecto true

          // Cargar configuración de módulos
          if (data.moduleSettings) {
            setModuleSettings(prevState => ({
              ...prevState,
              ...data.moduleSettings
            }));
          }

          // Cargar clientes seleccionados
          if (data.selectedClientes) {
            setSelectedClientes(data.selectedClientes);
          }

          // Cargar servicios seleccionados
          if (data.selectedServicios) {
            setSelectedServicios(data.selectedServicios);
          }
        }
      } catch (error) {
        console.error("Error al cargar filtros:", error);
        setSnackbarMessage("Error al cargar configuración: " + error.message);
        setSaveSuccess(false);
        setSnackbarOpen(true);
      } finally {
        setInitialLoading(false);
      }
    };

    // Cargar clientes y servicios disponibles
    const loadData = async () => {
      try {
        // Obtener los clientes asignados al admin
        const clientesAsignados = adminData?.clientes || adminData?.clientesAsignados || [];
        const tiendasAsignadas = adminData?.tiendas || adminData?.tiendasAsignadas || [];

        // Cargar solo los clientes asignados al admin
        if (clientesAsignados.length > 0) {
          const clientesPromises = clientesAsignados.map(async (clienteId) => {
            const clienteRef = doc(db, "servicios", currentService.id, "clientes", clienteId);
            const clienteDoc = await getDoc(clienteRef);
            if (clienteDoc.exists() && !clienteDoc.data().borrado) {
              return {
                id: clienteId,
                nombre: clienteDoc.data().nombre || `Cliente ${clienteId}`
              };
            }
            return null;
          });
          
          const clientesList = (await Promise.all(clientesPromises)).filter(Boolean);
          console.log("Clientes asignados al admin:", clientesList);
          setClientes(clientesList);
        } else {
          setClientes([]);
        }

        // Cargar solo los servicios asignados al admin
        if (tiendasAsignadas.length > 0) {
          const serviciosPromises = tiendasAsignadas.map(async (tiendaId) => {
            const tiendaRef = doc(db, "servicios", currentService.id, "tiendas", tiendaId);
            const tiendaDoc = await getDoc(tiendaRef);
            if (tiendaDoc.exists()) {
              const data = tiendaDoc.data();
              return {
                numTienda: data.numTienda || tiendaId,
                nombreTienda: data.nombre || `Tienda ${data.numTienda || tiendaId}`,
                nombreCliente: data.clienteNombre || data.cliente || null
              };
            }
            return null;
          });
          
          const serviciosList = (await Promise.all(serviciosPromises)).filter(Boolean);
          console.log("Servicios asignados al admin:", serviciosList);
          setServicios(serviciosList);
        } else {
          setServicios([]);
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setSnackbarMessage("Error al cargar datos: " + error.message);
        setSaveSuccess(false);
        setSnackbarOpen(true);
      }
    };

    loadUserFilters();
    loadData();
  }, [currentUser, currentService, adminData]);

  // Función para guardar configuración de filtros
  const saveFilters = async () => {
    if (!currentUser?.email || !currentService?.id) return;

    setSaveLoading(true);

    try {
      // Referencia al documento de configuración de filtros del usuario
      const userFilterRef = doc(
        db,
        "servicios",
        currentService.id,
        "usuarios_filtros",
        currentUser.email
      );

      // Preparar datos para guardar
      const filterData = {
        notificationsEnabled,
        moduleSettings,
        selectedClientes,
        selectedServicios,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email
      };

      // Guardar en Firestore
      await setDoc(userFilterRef, filterData, { merge: true });

      // Mostrar mensaje de éxito
      setSnackbarMessage("Configuración guardada correctamente");
      setSaveSuccess(true);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error al guardar filtros:", error);
      setSnackbarMessage("Error al guardar: " + error.message);
      setSaveSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  // Función para manejar cambio en la configuración de módulos
  const handleModuleSettingChange = (moduleId, setting) => {
    setModuleSettings(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [setting]: !prev[moduleId][setting]
      }
    }));
  };

  // Función para seleccionar/deseleccionar todos los módulos
  const handleToggleAllModules = (enabled) => {
    const updatedSettings = {};
    Object.keys(moduleSettings).forEach(moduleId => {
      updatedSettings[moduleId] = {
        ...moduleSettings[moduleId],
        enabled
      };
    });
    setModuleSettings(updatedSettings);
  };

  // Función para seleccionar/deseleccionar email para todos los módulos
  const handleToggleAllEmail = (enabled) => {
    const updatedSettings = {};
    Object.keys(moduleSettings).forEach(moduleId => {
      updatedSettings[moduleId] = {
        ...moduleSettings[moduleId],
        email: enabled
      };
    });
    setModuleSettings(updatedSettings);
  };

  // Filtrar servicios según los clientes seleccionados
  const filteredServicios = servicios.filter(servicio =>
    selectedClientes.length === 0 ||
    (servicio.cliente && (
      Array.isArray(servicio.cliente)
        ? servicio.cliente.some(c => selectedClientes.includes(c))
        : selectedClientes.includes(servicio.cliente)
    ))
  );

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: '#4678c3' }} />
      </Box>
    );
  }

  return (
    <div className="notification-filters-container">
      <Box sx={{ width: '100%' }}>
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" color="#4678c3" gutterBottom>
            Configuración General
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  color="primary"
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4678c3' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4678c3' } }}
                />
              }
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <NotificationsIcon style={{ marginRight: '8px', color: '#4678c3' }} />
                  <span>Recibir notificaciones</span>
                </div>
              }
            />
          </FormGroup>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" color="#4678c3" gutterBottom>
            Configuración por Módulo
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="60%">Módulo</TableCell>
                  <TableCell width="20%" align="center">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>Activado</span>
                      <Checkbox
                        indeterminate={Object.values(moduleSettings).some(m => m.enabled) && Object.values(moduleSettings).some(m => !m.enabled)}
                        checked={Object.values(moduleSettings).every(m => m.enabled)}
                        onChange={(e) => handleToggleAllModules(e.target.checked)}
                        sx={{ color: '#4678c3', '&.Mui-checked': { color: '#4678c3' } }}
                      />
                    </div>
                  </TableCell>
                  <TableCell width="20%" align="center">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>Email</span>
                      <Checkbox
                        indeterminate={Object.values(moduleSettings).some(m => m.email) && Object.values(moduleSettings).some(m => !m.email)}
                        checked={Object.values(moduleSettings).every(m => m.email)}
                        onChange={(e) => handleToggleAllEmail(e.target.checked)}
                        sx={{ color: '#4678c3', '&.Mui-checked': { color: '#4678c3' } }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map(module => (
                  <TableRow key={module.id}>
                    <TableCell component="th" scope="row">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '10px', color: '#4678c3' }}>{module.icon}</span>
                        <span>{module.name}</span>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={moduleSettings[module.id].enabled}
                        onChange={() => handleModuleSettingChange(module.id, 'enabled')}
                        sx={{ color: '#4678c3', '&.Mui-checked': { color: '#4678c3' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={moduleSettings[module.id].email}
                        disabled={!moduleSettings[module.id].enabled}
                        onChange={() => handleModuleSettingChange(module.id, 'email')}
                        sx={{
                          color: '#4678c3',
                          '&.Mui-checked': { color: '#4678c3' },
                          '&.Mui-disabled': { color: '#cccccc' }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper elevation={2} sx={{ p: 2, mb: 2, flex: 1, minWidth: '300px' }}>
            <Typography variant="h6" color="#4678c3" gutterBottom>
              Clientes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ClienteSearch
              clientes={clientes}
              selectedClientes={selectedClientes}
              onChange={setSelectedClientes}
              disabled={false}
              required={false}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              {selectedClientes.length === 0 ?
                "Recibirás notificaciones de todos los clientes asignados" :
                `Has seleccionado ${selectedClientes.length} cliente(s)`
              }
            </Typography>
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 2, flex: 1, minWidth: '300px' }}>
            <Typography variant="h6" color="#4678c3" gutterBottom>
              Servicios
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ServicioSearch
              servicios={servicios}
              selectedServicios={selectedServicios}
              onChange={setSelectedServicios}
              disabled={false}
              required={false}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              {selectedServicios.length === 0 ?
                "Recibirás notificaciones de todos los servicios asignados" :
                `Has seleccionado ${selectedServicios.length} servicio(s)`
              }
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={saveFilters}
            disabled={saveLoading}
            sx={{
              backgroundColor: '#4678c3',
              '&:hover': { backgroundColor: '#3a68a7' }
            }}
          >
            {saveLoading ? 'Guardando...' : 'Guardar configuración'}
          </Button>
        </Box>
      </Box>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={saveSuccess ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NotificationSettings;